import {
  Button,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  DeleteOutlined,
  EyeOutlined,
  InboxOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Key } from 'react';
import { useTranslation } from 'react-i18next';
import LinkButton from 'components/link-button';
import {
  deleteCommissionSnapshot,
  getCommissionSnapshotItems,
  getCommissionSnapshotList,
  importCommissionSnapshot,
} from 'apis/commission-snapshot';
import type {
  CommissionMonthSnapshotItem,
  SnapshotGroup,
} from 'types/commission-snapshot';
import formatTime from 'utils/formatTime';
import LOCALS from 'commons/locals';

const { Title } = Typography;

const PAGE_SIZE = 10;

const moneyText = (value?: string | null) => value || '-';
export const nowrapColumnTitle = (title: string) => (
  <span className="whitespace-nowrap">{title}</span>
);
export const nowrapCellText = (text: string) => (
  <span className="whitespace-nowrap">{text}</span>
);

const renderTime = (value?: string | null) =>
  nowrapCellText(value ? formatTime(value) : '-');

const withNowrapColumnTitles = <T extends object>(
  columns: ColumnsType<T>,
): ColumnsType<T> =>
  columns.map((column) => ({
    ...column,
    title:
      typeof column.title === 'string'
        ? nowrapColumnTitle(column.title)
        : column.title,
  })) as ColumnsType<T>;

type UploadFormValues = { name: string; remark?: string };
type ItemFilterValues = { orderId?: number | null; productId?: number | null };

export default function CommissionSnapshotPage() {
  const { t } = useTranslation();
  const [uploadForm] = Form.useForm<UploadFormValues>();
  const [itemForm] = Form.useForm<ItemFilterValues>();

  const productSourceMap: Record<number, { label: string; color: string }> = {
    0: { label: t(LOCALS.recycle), color: 'blue' },
    1: { label: t(LOCALS.consignment), color: 'green' },
    2: { label: t(LOCALS.commission_snapshot_self_owned), color: 'orange' },
  };

  // 快照主表
  const [list, setList] = useState<SnapshotGroup[]>([]);
  const [total, setTotal] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const pageSizeRef = useRef(PAGE_SIZE);
  const [listLoading, setListLoading] = useState(false);
  const [expandSearchResults, setExpandSearchResults] = useState(false);
  const latestListRequestRef = useRef(0);

  const fetchList = useCallback(
    async (
      page: number,
      values: ItemFilterValues = {},
      options: { expandResults?: boolean } = {},
    ) => {
      const requestId = latestListRequestRef.current + 1;
      latestListRequestRef.current = requestId;
      setListLoading(true);
      const hasFilter = Boolean(values.orderId || values.productId);
      try {
        let groups: SnapshotGroup[];
        let snapshotTotal: number;

        if (hasFilter) {
          const res = await getCommissionSnapshotItems({
            pageNum: page,
            pageSize: pageSizeRef.current,
            ...(values.orderId ? { orderId: values.orderId } : {}),
            ...(values.productId ? { productId: values.productId } : {}),
          });
          if (latestListRequestRef.current !== requestId) return;
          groups = res.list;
          snapshotTotal = res.total;
        } else {
          // 无筛选时用快照列表接口获取正确的快照总数，同时预加载明细以支持行展开
          const [listRes, itemsRes] = await Promise.all([
            getCommissionSnapshotList({ pageNum: page, pageSize: pageSizeRef.current }),
            getCommissionSnapshotItems({ pageNum: 1, pageSize: 9999 }),
          ]);
          if (latestListRequestRef.current !== requestId) return;
          const itemMap = new Map(itemsRes.list.map((g) => [g.id, g.items]));
          groups = listRes.list.map((s) => ({
            ...s,
            items: itemMap.get(s.id) ?? [],
          }));
          snapshotTotal = listRes.total;
        }

        setList(groups);
        setTotal(snapshotTotal);
        if (options.expandResults) {
          setExpandedRowKeys(groups.map((s) => s.id));
        }
      } finally {
        if (latestListRequestRef.current === requestId) {
          setListLoading(false);
        }
      }
    },
    [],
  );

  useEffect(() => {
    fetchList(1);
  }, [fetchList]);

  // 上传 Modal
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);

  const [expandedRowKeys, setExpandedRowKeys] = useState<Key[]>([]);

  const handleUpload = async () => {
    if (!uploadFile) {
      message.error(t(LOCALS.commission_snapshot_select_file_first));
      return;
    }
    const values = await uploadForm.validateFields();
    setUploadLoading(true);
    try {
      const res = await importCommissionSnapshot({
        file: uploadFile,
        name: values.name,
        remark: values.remark,
      });
      message.success(
        t(LOCALS.commission_snapshot_create_success, {
          itemCount: res.itemCount,
          totalCommission: moneyText(res.totalCommission),
        }),
      );
      setUploadOpen(false);
      uploadForm.resetFields();
      setUploadFile(null);
      setPageNum(1);
      setExpandSearchResults(false);
      setExpandedRowKeys([]);
      itemForm.resetFields();
      fetchList(1);
    } finally {
      setUploadLoading(false);
    }
  };

  const handleUploadCancel = () => {
    setUploadOpen(false);
    uploadForm.resetFields();
    setUploadFile(null);
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteCommissionSnapshot(id);
      message.success(t(LOCALS.commission_snapshot_delete_success));
      setExpandedRowKeys((keys) => keys.filter((key) => key !== id));
      fetchList(pageNum, itemForm.getFieldsValue(), {
        expandResults: expandSearchResults,
      });
    } catch {
      // global axios interceptor handles error toast
    }
  };

  const handleViewItems = (snapshot: SnapshotGroup) => {
    setExpandedRowKeys([snapshot.id]);
  };

  const handleItemSearch = (values: ItemFilterValues) => {
    if (!values.orderId && !values.productId) return;
    setPageNum(1);
    setExpandSearchResults(true);
    fetchList(1, values, { expandResults: true });
  };

  const handleItemReset = () => {
    itemForm.resetFields();
    setPageNum(1);
    setExpandSearchResults(false);
    setExpandedRowKeys([]);
    fetchList(1);
  };

  const clearExpandedRows = () => {
    setExpandedRowKeys([]);
  };

  const snapshotColumns: ColumnsType<SnapshotGroup> = withNowrapColumnTitles([
    {
      title: t(LOCALS.commission_snapshot_snapshot_name),
      dataIndex: 'name',
      width: 180,
      ellipsis: true,
    },
    {
      title: t(LOCALS.commission_snapshot_source_file),
      dataIndex: 'sourceFileName',
      width: 200,
      ellipsis: true,
      render: (v: string | null) => v ?? '-',
    },
    {
      title: t(LOCALS.commission_snapshot_importer),
      dataIndex: 'createdBy',
      width: 150,
      render: (v: string | null) => v ?? '-',
    },
    {
      title: t(LOCALS.created_time),
      dataIndex: 'createdAt',
      width: 160,
      render: renderTime,
    },
    {
      title: t(LOCALS.remark),
      dataIndex: 'remark',
      ellipsis: true,
      render: (v: string | null) => v ?? '-',
    },
    {
      title: t(LOCALS.operation),
      width: 130,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleViewItems(record)}
          >
            {t(LOCALS.view)}
          </Button>
          <Popconfirm
            title={t(LOCALS.commission_snapshot_confirm_delete_title)}
            description={t(LOCALS.commission_snapshot_confirm_delete_desc)}
            onConfirm={() => handleDelete(record.id)}
            okText={t(LOCALS.confirm)}
            cancelText={t(LOCALS.cancel)}
          >
            <Button type="link" danger icon={<DeleteOutlined />} size="small">
              {t(LOCALS.delete)}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]);

  const itemColumns: ColumnsType<CommissionMonthSnapshotItem> = withNowrapColumnTitles([
    {
      title: t(LOCALS.commission_snapshot_order_id),
      dataIndex: 'orderId',
      width: 110,
      render: (v: number | null) =>
        v ? <LinkButton href={`/oms/order-view/${v}`}>{v}</LinkButton> : '-',
    },
    {
      title: t(LOCALS.commission_snapshot_employee_name),
      dataIndex: 'employeeName',
      width: 110,
      render: (v: string | null) => v ?? '-',
    },
    {
      title: t(LOCALS.commission_snapshot_order_create_time),
      dataIndex: 'orderCreateTime',
      width: 160,
      render: renderTime,
    },
    {
      title: t(LOCALS.commission_snapshot_payment_day),
      dataIndex: 'paymentTime',
      width: 160,
      render: renderTime,
    },
    {
      title: t(LOCALS.commission_snapshot_receive_time),
      dataIndex: 'receiveTime',
      width: 160,
      render: renderTime,
    },
    {
      title: t(LOCALS.currency),
      dataIndex: 'currency',
      width: 80,
      render: (v: string | null) => v ?? '-',
    },
    {
      title: t(LOCALS.commission_snapshot_product_id),
      dataIndex: 'productId',
      width: 100,
      render: (v: number | null) =>
        v ? <LinkButton href={`/pms/product-view/${v}`}>{v}</LinkButton> : '-',
    },
    {
      title: t(LOCALS.product_source),
      dataIndex: 'productSource',
      width: 90,
      render: (v: 0 | 1 | 2 | null) => {
        if (v == null) return '-';
        const cfg = productSourceMap[v];
        return cfg ? <Tag color={cfg.color}>{cfg.label}</Tag> : v;
      },
    },
    { title: t(LOCALS.commission_snapshot_product_quantity), dataIndex: 'productQuantity', width: 90 },
    {
      title: t(LOCALS.commission_snapshot_is_tax_free),
      dataIndex: 'isTaxFree',
      width: 90,
      render: (v: 0 | 1) => (v === 1 ? t(LOCALS.yes) : t(LOCALS.no)),
    },
    {
      title: t(LOCALS.commission_snapshot_order_total_tax),
      dataIndex: 'orderTotalTax',
      width: 120,
      align: 'right',
      render: moneyText,
    },
    {
      title: t(LOCALS.commission_snapshot_product_cost_price),
      dataIndex: 'productCostPrice',
      width: 110,
      align: 'right',
      render: moneyText,
    },
    {
      title: t(LOCALS.commission_snapshot_item_pay_amount),
      dataIndex: 'itemAllocatedPayAmount',
      width: 150,
      align: 'right',
      render: moneyText,
    },
    {
      title: t(LOCALS.commission_snapshot_item_commission),
      dataIndex: 'itemAllocatedCommission',
      width: 130,
      align: 'right',
      render: moneyText,
    },
    {
      title: t(LOCALS.commission_snapshot_staff_in_charge),
      dataIndex: 'staffName',
      width: 110,
      render: (v: string | null) => v ?? '-',
    },
    {
      title: t(LOCALS.commission_snapshot_reception_staff_name),
      dataIndex: 'receptionStaffName',
      width: 130,
      render: (v: string | null) => v ?? '-',
    },
  ]);

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <Title level={4} style={{ margin: 0 }}>
          {t(LOCALS.commission_snapshot)}
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setUploadOpen(true)}
        >
          {t(LOCALS.commission_snapshot_upload_excel)}
        </Button>
      </div>

      <Form
        form={itemForm}
        layout="inline"
        onFinish={handleItemSearch}
        className="mb-4"
      >
        <Form.Item name="orderId" label={t(LOCALS.commission_snapshot_order_id)} className="mb-0">
          <InputNumber
            placeholder={t(LOCALS.commission_snapshot_input_order_id) || ''}
            controls={false}
            style={{ width: 150 }}
          />
        </Form.Item>
        <Form.Item name="productId" label={t(LOCALS.commission_snapshot_product_id)} className="mb-0">
          <InputNumber
            placeholder={t(LOCALS.commission_snapshot_input_product_id) || ''}
            controls={false}
            style={{ width: 150 }}
          />
        </Form.Item>
        <Form.Item className="mb-0">
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SearchOutlined />}
            >
              {t(LOCALS.search)}
            </Button>
            <Button onClick={handleItemReset}>{t(LOCALS.reset)}</Button>
          </Space>
        </Form.Item>
      </Form>

      <Table
        rowKey="id"
        loading={listLoading}
        columns={snapshotColumns}
        dataSource={list}
        scroll={{ x: 1100 }}
        rowClassName={(record) =>
          expandedRowKeys.includes(record.id) ? 'ant-table-row-selected' : ''
        }
        expandable={{
          expandedRowKeys,
          showExpandColumn: false,
          expandedRowRender: (record) => {
            return (
              <div className="py-4">
                <Table
                  rowKey="id"
                  columns={itemColumns}
                  dataSource={record.items}
                  scroll={{ x: 1900 }}
                  size="small"
                  pagination={{
                    defaultPageSize: 100,
                    showSizeChanger: true,
                    showTotal: (n) =>
                      t(LOCALS.commission_snapshot_total_count, { total: n }),
                  }}
                />
              </div>
            );
          },
        }}
        pagination={{
          current: pageNum,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (n) =>
            t(LOCALS.commission_snapshot_total_count, { total: n }),
          onChange: (page, newPageSize) => {
            setPageNum(page);
            if (newPageSize !== pageSizeRef.current) {
              pageSizeRef.current = newPageSize;
              setPageSize(newPageSize);
            }
            clearExpandedRows();
            fetchList(page, itemForm.getFieldsValue(), {
              expandResults: expandSearchResults,
            });
          },
        }}
      />

      <Modal
        title={t(LOCALS.commission_snapshot_upload_modal_title)}
        open={uploadOpen}
        onOk={handleUpload}
        onCancel={handleUploadCancel}
        okText={t(LOCALS.commission_snapshot_upload_and_parse)}
        cancelText={t(LOCALS.cancel)}
        confirmLoading={uploadLoading}
        destroyOnClose
      >
        <Form form={uploadForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label={t(LOCALS.commission_snapshot_snapshot_name)}
            rules={[
              {
                required: true,
                message: t(LOCALS.commission_snapshot_please_enter_name) || '',
              },
              {
                max: 100,
                message: t(LOCALS.commission_snapshot_max_100_chars) || '',
              },
            ]}
          >
            <Input
              placeholder={t(LOCALS.commission_snapshot_name_placeholder) || ''}
            />
          </Form.Item>
          <Form.Item
            name="remark"
            label={t(LOCALS.remark)}
            rules={[
              {
                max: 500,
                message: t(LOCALS.commission_snapshot_max_500_chars) || '',
              },
            ]}
          >
            <Input.TextArea
              rows={2}
              placeholder={t(LOCALS.commission_snapshot_optional) || ''}
            />
          </Form.Item>
          <Form.Item label={t(LOCALS.commission_snapshot_excel_file)} required>
            <Upload.Dragger
              accept=".xlsx,.xls"
              maxCount={1}
              beforeUpload={(file) => {
                setUploadFile(file);
                return false;
              }}
              onRemove={() => setUploadFile(null)}
              fileList={
                uploadFile
                  ? [{ uid: '1', name: uploadFile.name, status: 'done' }]
                  : []
              }
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                {t(LOCALS.commission_snapshot_drag_hint)}
              </p>
              <p className="ant-upload-hint">
                {t(LOCALS.commission_snapshot_format_hint)}
              </p>
            </Upload.Dragger>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
